import React, { useState, useEffect } from "react";
import {
  Flex,
  Button,
  Input,
  Dialog,
  List,
  Text,
  Label,
  Dropdown,
  CloseIcon,
  EditIcon,
  AddIcon,
  Loader,
} from "@fluentui/react-northstar";
import axios from "axios";
import { api } from "../../../../../Assets/api/api";
import "./CommentBank.scss";

const COMMENT_TAGS = [
  { key: "positive", value: "Positive", color: "#92C353" },
  { key: "suggestion", value: "Suggestion", color: "#F8D347" },
  { key: "issue", value: "Issue", color: "#E2726E" },
  { key: "grammar", value: "Grammar", color: "#8B9DC3" },
  { key: "structure", value: "Structure", color: "#6264A7" },
  { key: "citation", value: "Citation", color: "#FF8C00" },
];

const DeleteConfirmDialog = ({ open, onClose, onConfirm, isDeleting }) => {
  return (
    <Dialog
      open={open}
      onOpen={() => {}}
      onCancel={onClose}
      style={{ width: "400px" }}
      header="Delete Comment Template"
      content={
        <Flex column gap="gap.medium" padding="padding.medium">
          <Text>Are you sure you want to delete this comment template?</Text>
          <Flex gap="gap.small" hAlign="end">
            <Button
              content="Cancel"
              onClick={onClose}
              disabled={isDeleting}
              className="cancel-comment-bank-button"
            />
            <Button
              content="Delete"
              onClick={onConfirm}
              disabled={isDeleting}
              loading={isDeleting}
              className="delete-comment-bank-button"
            />
          </Flex>
        </Flex>
      }
      headerAction={{
        icon: <CloseIcon />,
        title: "Close",
        onClick: onClose,
      }}
    />
  );
};

const CommentBankDialog = ({
  open,
  onClose,
  onSubmit,
  editingComment,
  isSubmitting,
  userEmail,
}) => {
  const [comment, setComment] = useState(editingComment?.text || "");
  const [selectedTag, setSelectedTag] = useState(
    editingComment
      ? COMMENT_TAGS.find((tag) => tag.value === editingComment.tag) ||
          COMMENT_TAGS[0]
      : COMMENT_TAGS[0]
  );

  // Reset form when dialog opens/closes or when editing different comment
  useEffect(() => {
    if (open) {
      if (editingComment) {
        setComment(editingComment.text);
        const tag =
          COMMENT_TAGS.find((t) => t.value === editingComment.tag) ||
          COMMENT_TAGS[0];
        setSelectedTag(tag);
      } else {
        // Reset form for new comment
        setComment("");
        setSelectedTag(COMMENT_TAGS[0]);
      }
    }
  }, [open, editingComment]);

  const handleSubmit = () => {
    if (comment.trim()) {
      onSubmit({
        text: comment.trim(),
        tag: selectedTag.value,
        userEmail: userEmail,
      });
    }
  };

  const handleClose = () => {
    setComment("");
    setSelectedTag(COMMENT_TAGS[0]);
    onClose();
  };

  const tagOptions = COMMENT_TAGS.map((tag) => ({
    content: (
      <Flex vAlign="center" gap="gap.small">
        <Label
          circular
          styles={{
            backgroundColor: `${tag.color}20`,
            color: tag.color,
          }}
          content={tag.value}
        />
      </Flex>
    ),
    key: tag.key,
    value: tag.value,
  }));

  return (
    <Dialog
      open={open}
      onOpen={() => {}}
      onCancel={handleClose}
      style={{ width: "600px" }}
      header={editingComment ? "Edit Comment" : "Add Comment"}
      content={
        <Flex column gap="gap.medium" padding="padding.medium">
          <Flex column gap="gap.small">
            <Text size="small" weight="semibold">
              Tag
            </Text>
            <Dropdown
              items={tagOptions}
              placeholder="Select tag"
              value={selectedTag.value}
              onChange={(e, { value }) => {
                const tag = COMMENT_TAGS.find((t) => t.value === value.value);
                if (tag) setSelectedTag(tag);
              }}
              fluid
              styles={{ maxWidth: "200px" }}
              disabled={isSubmitting}
              className="comment-tag-dropdown"
            />
            <Text size="small" weight="semibold">
              Comment Template
            </Text>
            <Input
              placeholder="Type your comment here..."
              value={comment}
              onChange={(e, { value }) => setComment(value)}
              fluid
              multiline
              disabled={isSubmitting}
              className="comment-template-input"
            />
          </Flex>
          <Flex gap="gap.small" hAlign="end" className="comment-action-wrapper">
            <Button
              content="Cancel"
              onClick={handleClose}
              disabled={isSubmitting}
              className="cancel-comment-bank-button"
            />
            <Button
              content={editingComment ? "Save Changes" : "Add Comment"}
              primary
              onClick={handleSubmit}
              disabled={!comment.trim() || isSubmitting}
              loading={isSubmitting}
              className="save-comment-bank-button"
            />
          </Flex>
        </Flex>
      }
      headerAction={{
        icon: <CloseIcon />,
        title: "Close",
        onClick: handleClose,
      }}
    />
  );
};

const CommentBank = ({ user }) => {
  const [comments, setComments] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingComment, setEditingComment] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    commentId: null,
  });

  const fetchComments = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get(
        `/api/comment-bank/${user.mail}${user.slug}`
      );
      setComments(response.data.result);
    } catch (err) {
      setError("Failed to load comments. Please try again.");
      console.error("Error fetching comments:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
  }, []);

  const handleAddComment = async (comment) => {
    try {
      setIsSubmitting(true);
      setError(null);
      if (editingComment) {
        // Update existing comment
        await api.put(
          `/api/comment-bank/${editingComment.id}${user.slug}`,
          comment
        );
      } else {
        // Create new comment
        await api.post(`/api/comment-bank${user.slug}`, comment);
      }
      await fetchComments(); // Refresh the list
      handleCloseDialog();
    } catch (err) {
      setError(
        editingComment ? "Failed to update comment." : "Failed to add comment."
      );
      console.error("Error saving comment:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteComment = async () => {
    if (!deleteConfirm.commentId) return;

    try {
      setIsSubmitting(true);
      setError(null);
      await api.delete(
        `/api/comment-bank/${deleteConfirm.commentId}${user.slug}`
      );
      await fetchComments();
      setDeleteConfirm({ open: false, commentId: null });
    } catch (err) {
      setError("Failed to delete comment.");
      console.error("Error deleting comment:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingComment(null);
    setError(null);
  };

  if (isLoading) {
    return (
      <Flex hAlign="center" vAlign="center" style={{ height: "200px" }}>
        <Loader />
      </Flex>
    );
  }

  return (
    <div className="comment-bank-container">
      <Flex
        column
        gap="gap.medium"
        padding="padding.medium"
        className="comment-list-section"
      >
        <Flex space="between" vAlign="center">
          <Text size="large" weight="semibold">
            Comment Bank
          </Text>
          <Button
            icon={<AddIcon />}
            content="Add Comment"
            primary
            onClick={() => setIsDialogOpen(true)}
            disabled={isSubmitting}
            className="add-comment-bank-button"
          />
        </Flex>

        {error && (
          <Flex
            padding="padding.medium"
            style={{ background: "#FDE7E9", borderRadius: "4px" }}
            className="error-message"
          >
            <Text color="red">{error}</Text>
          </Flex>
        )}

        {comments.length === 0 && !isLoading ? (
          <Flex hAlign="center" padding="padding.medium">
            <Text size="medium" color="grey">
              No comment templates yet. Click "Add Template" to create one.
            </Text>
          </Flex>
        ) : (
          <List
            items={comments.map((comment) => ({
              key: comment.id,
              content: (
                <Flex
                  vAlign="center"
                  gap="gap.small"
                  padding="padding.medium"
                  className="comment-item"
                >
                  <Label
                    circular
                    styles={{
                      backgroundColor: `${
                        COMMENT_TAGS.find((t) => t.value === comment.tag)?.color
                      }20`,
                      color: COMMENT_TAGS.find((t) => t.value === comment.tag)
                        ?.color,
                      fontSize: "12px",
                      padding: "4px 8px",
                    }}
                    content={comment.tag}
                  />
                  <Text style={{ flex: 1 }}>{comment.text}</Text>
                  <Flex gap="gap.smaller">
                    <Button
                      icon={<EditIcon />}
                      iconOnly
                      title="Edit template"
                      onClick={() => {
                        setEditingComment(comment);
                        setIsDialogOpen(true);
                      }}
                      disabled={isSubmitting}
                    />
                    <Button
                      icon={<CloseIcon />}
                      iconOnly
                      title="Delete template"
                      style={{ color: "red" }}
                      onClick={() =>
                        setDeleteConfirm({ open: true, commentId: comment.id })
                      }
                      disabled={isSubmitting}
                    />
                  </Flex>
                </Flex>
              ),
            }))}
          />
        )}

        <CommentBankDialog
          open={isDialogOpen}
          onClose={handleCloseDialog}
          onSubmit={handleAddComment}
          editingComment={editingComment}
          isSubmitting={isSubmitting}
          userEmail={user.mail}
        />

        <DeleteConfirmDialog
          open={deleteConfirm.open}
          onClose={() => setDeleteConfirm({ open: false, commentId: null })}
          onConfirm={handleDeleteComment}
          isDeleting={isSubmitting}
        />
      </Flex>
    </div>
  );
};

export default CommentBank;
